import { ComponentType, lazy } from 'react'

/**
 * A wrapper around React.lazy() that handles chunk load errors gracefully.
 *
 * When a new deployment occurs, old JS chunks are removed from the server.
 * If a user still has the old app open and navigates to a lazy-loaded route,
 * the import will fail with a "Failed to fetch dynamically imported module" error.
 *
 * This wrapper catches that error and forces a single page reload so the user
 * picks up the new chunks. It uses sessionStorage to prevent infinite reload
 * loops — if we already tried reloading for this specific import, we let the
 * error propagate to the error boundary instead.
 */
function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(() =>
    importFn().catch((error: Error) => {
      // Build a storage key from the error message or a hash of the import function
      // to track whether we've already attempted a reload for this chunk.
      const storageKey = `chunk-retry-${hashCode(importFn.toString())}`
      const hasRetried = sessionStorage.getItem(storageKey)

      if (!hasRetried) {
        // Mark that we're about to retry so we don't loop forever
        sessionStorage.setItem(storageKey, '1')
        // Reload the page to pick up the new chunks
        window.location.reload()
        // Return a never-resolving promise to prevent React from rendering
        // anything before the reload takes effect
        return new Promise<{ default: T }>(() => {})
      }

      // We already tried reloading once — clear the flag and let the error
      // propagate to the error boundary so the user sees a helpful message.
      sessionStorage.removeItem(storageKey)
      throw error
    })
  )
}

/**
 * Simple string hash to create a short, deterministic key from the import
 * function's stringified source.
 */
function hashCode(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash |= 0 // Convert to 32-bit integer
  }
  return Math.abs(hash)
}

export { lazyWithRetry }
